import { Check, MessageSquare, ShieldQuestion, X } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { InteractionRequest } from '@/lib/agent/types';
import { StreamingMarkdown } from '@/lib/markdown';
import { cn } from '@/lib/utils/cn';
import { humanizeToolName } from './blocks/helpers';

type InteractionDecision = 'allow' | 'deny';

const INTERACTION_DRAFT_KEY_PREFIX = 'antioch:agent-interaction-draft:';

export interface InteractionPanelsProps {
  pending: InteractionRequest[];
  canRespond: boolean;
  onRespond: (
    requestId: string,
    decision: InteractionDecision,
    updatedInput?: Record<string, unknown>,
    message?: string
  ) => void;
}

interface InteractionPanelProps {
  request: InteractionRequest;
  canRespond: boolean;
  onRespond: InteractionPanelsProps['onRespond'];
}

export interface NormalizedQuestionOption {
  value: string;
  label: string;
  answerValue: string;
  description?: string;
}

export interface NormalizedQuestion {
  key: string;
  answerKey: string;
  title: string;
  header?: string;
  multiSelect: boolean;
  options: NormalizedQuestionOption[];
}

export interface QuestionAnswerDraft {
  selected: string[];
  other: string;
}

export type QuestionAnswerDrafts = Record<string, QuestionAnswerDraft>;

export function normalizeAskUserQuestions(input: Record<string, unknown>): NormalizedQuestion[] {
  const rawQuestions = Array.isArray(input.questions) ? input.questions : [];
  return rawQuestions.map((raw, index) => {
    const question = isRecord(raw) ? raw : {};
    const key = stringValue(question.id) ?? stringValue(question.key) ?? String(index);
    const title =
      stringValue(question.question) ??
      stringValue(question.prompt) ??
      stringValue(question.title) ??
      stringValue(question.header) ??
      'Question';
    const rawOptions = Array.isArray(question.options) ? question.options : [];
    return {
      key,
      answerKey: stringValue(question.question) ?? title,
      title: antiochAgentText(title) ?? title,
      header: antiochAgentText(stringValue(question.header)),
      multiSelect: question.multiSelect === true || question.multi_select === true,
      options: rawOptions.map(normalizeOption).filter((option): option is NormalizedQuestionOption => option !== null),
    };
  });
}

export function buildAskUserQuestionAnswers(
  input: Record<string, unknown>,
  drafts: QuestionAnswerDrafts
): Record<string, string | string[]> {
  const answers: Record<string, string | string[]> = {};
  for (const question of normalizeAskUserQuestions(input)) {
    const draft = drafts[question.key] ?? { selected: [], other: '' };
    const other = draft.other.trim();
    const selectedAnswers = selectedAnswerValues(question, draft.selected);
    const values = other ? [...selectedAnswers, other] : selectedAnswers;
    answers[question.answerKey] = question.multiSelect ? values : other || selectedAnswers[0] || '';
  }
  return answers;
}

export function buildAskUserQuestionUpdatedInput(
  input: Record<string, unknown>,
  answers: Record<string, string | string[]>
): Record<string, unknown> {
  return {
    questions: Array.isArray(input.questions) ? input.questions : [],
    answers,
  };
}

export function InteractionPanels({ pending, canRespond, onRespond }: InteractionPanelsProps) {
  if (pending.length === 0) return null;
  return (
    <div className="flex w-full max-w-[680px] flex-col gap-2 pb-1">
      {pending.map((request) => (
        <InteractionPanel key={request.request_id} request={request} canRespond={canRespond} onRespond={onRespond} />
      ))}
    </div>
  );
}

function InteractionPanel({ request, canRespond, onRespond }: InteractionPanelProps) {
  if (request.kind === 'ask_user_question') {
    return <AskUserQuestionPanel request={request} canRespond={canRespond} onRespond={onRespond} />;
  }
  if (request.kind === 'exit_plan_mode') {
    return <ExitPlanModePanel request={request} canRespond={canRespond} onRespond={onRespond} />;
  }
  return <PermissionPanel request={request} canRespond={canRespond} onRespond={onRespond} />;
}

function AskUserQuestionPanel({ request, canRespond, onRespond }: InteractionPanelProps) {
  const questions = React.useMemo(() => normalizeAskUserQuestions(request.input), [request.input]);
  const draftStorageKey = React.useMemo(() => interactionDraftStorageKey(request), [request]);
  const [drafts, setDrafts] = React.useState<QuestionAnswerDrafts>(() =>
    readQuestionDrafts(draftStorageKey, questions)
  );
  const [focusedOtherKey, setFocusedOtherKey] = React.useState<string | null>(null);
  const otherInputRefs = React.useRef<Record<string, HTMLTextAreaElement | null>>({});

  React.useEffect(() => {
    setDrafts(readQuestionDrafts(draftStorageKey, questions));
    setFocusedOtherKey(null);
  }, [draftStorageKey, questions]);

  const updateDrafts = React.useCallback(
    (update: (prev: QuestionAnswerDrafts) => QuestionAnswerDrafts) => {
      setDrafts((prev) => {
        const next = update(prev);
        writeQuestionDrafts(draftStorageKey, next);
        return next;
      });
    },
    [draftStorageKey]
  );

  function setSingle(question: NormalizedQuestion, value: string) {
    updateDrafts((prev) => ({
      ...prev,
      [question.key]: { selected: [value], other: '' },
    }));
  }

  function toggleMulti(question: NormalizedQuestion, value: string) {
    updateDrafts((prev) => {
      const current = prev[question.key] ?? { selected: [], other: '' };
      const selected = current.selected.includes(value)
        ? current.selected.filter((item) => item !== value)
        : [...current.selected, value];
      return { ...prev, [question.key]: { ...current, selected } };
    });
  }

  function setOther(question: NormalizedQuestion, other: string) {
    updateDrafts((prev) => {
      const current = prev[question.key] ?? { selected: [], other: '' };
      return {
        ...prev,
        [question.key]: {
          selected: question.multiSelect ? current.selected : [],
          other,
        },
      };
    });
  }

  function activateOther(question: NormalizedQuestion) {
    setFocusedOtherKey(question.key);
    if (question.multiSelect) return;
    updateDrafts((prev) => {
      const current = prev[question.key] ?? { selected: [], other: '' };
      if (current.selected.length === 0) return prev;
      return {
        ...prev,
        [question.key]: {
          selected: [],
          other: current.other,
        },
      };
    });
  }

  function submit() {
    const answers = buildAskUserQuestionAnswers(request.input, drafts);
    clearQuestionDrafts(draftStorageKey);
    onRespond(request.request_id, 'allow', buildAskUserQuestionUpdatedInput(request.input, answers));
  }

  function focusOther(question: NormalizedQuestion, event: React.MouseEvent<HTMLDivElement>) {
    if (!canRespond) return;
    if (event.target instanceof HTMLTextAreaElement) return;
    event.preventDefault();
    activateOther(question);
    otherInputRefs.current[question.key]?.focus();
  }

  const complete = questions.every((question) => questionComplete(question, drafts));

  return (
    <InteractionShell icon={<MessageSquare className="size-4" />} title="Antioch Agent has some questions">
      <InteractionSurface>
        <div className="flex flex-col gap-4">
          {questions.length === 0 && (
            <p className="text-[13px] text-gray-600 leading-5 dark:text-gray-300">
              Continue without additional answers.
            </p>
          )}
          {questions.map((question, index) => {
            const draft = drafts[question.key] ?? { selected: [], other: '' };
            const otherActive = focusedOtherKey === question.key || draft.other.trim().length > 0;
            return (
              <div key={question.key} className="flex min-w-0 flex-col gap-1.5">
                <div className="flex min-w-0 items-start gap-2">
                  <span className="flex size-5 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white font-medium text-[11px] text-gray-600 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-300">
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13.5px] text-gray-800 leading-5 dark:text-gray-200">{question.title}</div>
                  </div>
                </div>
                {question.options.length > 0 && (
                  <div className="ml-7 grid gap-0.5">
                    {question.options.map((option) => {
                      const checked = draft.selected.includes(option.value);
                      return (
                        <button
                          key={option.value}
                          type="button"
                          aria-pressed={checked}
                          disabled={!canRespond}
                          onClick={() =>
                            question.multiSelect
                              ? toggleMulti(question, option.value)
                              : setSingle(question, option.value)
                          }
                          className={cn(
                            'group flex min-w-0 cursor-pointer items-start gap-2 rounded-lg px-2.5 py-1.5 text-left transition-colors',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300/70 dark:focus-visible:ring-white/25',
                            checked
                              ? 'bg-white dark:bg-white/[0.08]'
                              : 'bg-transparent hover:bg-white/70 dark:hover:bg-white/[0.05]',
                            !canRespond && 'cursor-not-allowed opacity-60'
                          )}
                        >
                          <span
                            aria-hidden
                            className={cn(
                              'mt-0.5 flex size-3.5 shrink-0 items-center justify-center border transition-colors',
                              question.multiSelect ? 'rounded-[4px]' : 'rounded-full',
                              checked
                                ? 'border-gray-800 bg-gray-800 text-white dark:border-gray-100 dark:bg-gray-100 dark:text-gray-900'
                                : 'border-gray-300 bg-white group-hover:border-gray-400 dark:border-white/20 dark:bg-transparent dark:group-hover:border-white/35'
                            )}
                          >
                            {question.multiSelect && checked ? <Check className="size-2.5" strokeWidth={3} /> : null}
                            {!question.multiSelect && checked ? (
                              <span className="size-1.5 rounded-full bg-current" />
                            ) : null}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block font-medium text-[13px] text-gray-900 leading-snug dark:text-gray-100">
                              {option.label}
                            </span>
                            {option.description && (
                              <span className="mt-1 block text-[12px] text-gray-500 leading-snug dark:text-gray-400">
                                {option.description}
                              </span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
                <div
                  onMouseDown={(event) => focusOther(question, event)}
                  className={cn(
                    'ml-7 flex min-h-11 w-[calc(100%-1.75rem)] cursor-text items-start gap-2 pl-2.5 transition-colors',
                    !canRespond && 'opacity-60'
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'mt-2 flex size-3.5 shrink-0 items-center justify-center border transition-colors',
                      question.multiSelect ? 'rounded-[4px]' : 'rounded-full',
                      otherActive
                        ? 'border-gray-800 bg-gray-800 text-white dark:border-gray-100 dark:bg-gray-100 dark:text-gray-900'
                        : 'border-gray-300 bg-white dark:border-white/20 dark:bg-transparent'
                    )}
                  >
                    {question.multiSelect && otherActive ? <Check className="size-2.5" strokeWidth={3} /> : null}
                    {!question.multiSelect && otherActive ? (
                      <span className="size-1.5 rounded-full bg-current" />
                    ) : null}
                  </span>
                  <div
                    className={cn(
                      'min-h-11 flex-1 rounded-lg border px-2.5 py-2 transition-colors',
                      'focus-within:border-gray-300 focus-within:ring-2 focus-within:ring-gray-300/30',
                      otherActive
                        ? 'border-gray-200 bg-white dark:border-white/15 dark:bg-white/[0.08]'
                        : 'border-gray-200 bg-white dark:border-white/10 dark:bg-white/[0.03]'
                    )}
                  >
                    <textarea
                      ref={(node) => {
                        otherInputRefs.current[question.key] = node;
                      }}
                      value={draft.other}
                      disabled={!canRespond}
                      aria-label={`Other answer for ${question.title}`}
                      onChange={(event) => setOther(question, event.target.value)}
                      onFocus={() => activateOther(question)}
                      onBlur={() => setFocusedOtherKey(null)}
                      placeholder={question.multiSelect ? 'Add another answer' : 'Type a different answer'}
                      rows={2}
                      className={cn(
                        'min-h-7 w-full resize-none border-0 bg-transparent p-0 text-[13px] text-gray-800 leading-snug outline-none',
                        'placeholder:text-gray-400 disabled:cursor-not-allowed dark:text-gray-100'
                      )}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </InteractionSurface>
      <InteractionActions>
        <Button
          type="button"
          size="sm"
          disabled={!canRespond || !complete}
          onClick={submit}
          className="h-8 gap-1.5 px-3"
        >
          <Check className="size-3.5" />
          Send
        </Button>
      </InteractionActions>
    </InteractionShell>
  );
}

function ExitPlanModePanel({ request, canRespond, onRespond }: InteractionPanelProps) {
  const [feedback, setFeedback] = React.useState('');
  const plan = planText(request.input);
  return (
    <InteractionShell icon={<ShieldQuestion className="size-4" />} title="Antioch Agent needs plan approval">
      <InteractionSurface>
        <StreamingMarkdown
          closeUnterminated={false}
          className="text-[13px] leading-relaxed [&>:first-child]:mt-0 [&>:last-child]:mb-0"
        >
          {plan}
        </StreamingMarkdown>
      </InteractionSurface>
      <textarea
        value={feedback}
        disabled={!canRespond}
        aria-label="Plan feedback or requested changes"
        onChange={(event) => setFeedback(event.target.value)}
        placeholder="Request changes"
        rows={3}
        className={cn(
          'mt-2.5 block w-full resize-none rounded-md border border-gray-200 bg-white px-2.5 py-2 text-[13px] text-gray-800 outline-none transition-colors',
          'placeholder:text-gray-400 focus:border-gray-300 disabled:opacity-60 dark:border-white/10 dark:bg-white/[0.03] dark:text-gray-100 dark:focus:border-white/20'
        )}
      />
      <InteractionActions>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!canRespond}
          onClick={() =>
            onRespond(
              request.request_id,
              'deny',
              undefined,
              feedback.trim() || 'Please revise the plan before executing'
            )
          }
          className="h-8 gap-1.5 px-3"
        >
          <X className="size-3.5" />
          Request changes
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={!canRespond}
          onClick={() => onRespond(request.request_id, 'allow', request.input)}
          className="h-8 gap-1.5 px-3"
        >
          <Check className="size-3.5" />
          Approve and execute
        </Button>
      </InteractionActions>
    </InteractionShell>
  );
}

function PermissionPanel({ request, canRespond, onRespond }: InteractionPanelProps) {
  const [message, setMessage] = React.useState('');
  const details = permissionInputText(request.tool_name, request.input);
  return (
    <InteractionShell
      icon={<ShieldQuestion className="size-4" />}
      title={`Permission: ${toolDisplayName(request.tool_name)}`}
    >
      <InteractionSurface className="agent-scroll max-h-56 overflow-auto p-2.5">
        <pre className="whitespace-pre-wrap text-[12px] text-gray-700 dark:text-gray-200">{details}</pre>
      </InteractionSurface>
      <Input
        value={message}
        disabled={!canRespond}
        onChange={(event) => setMessage(event.target.value)}
        placeholder="Optional denial message"
        className={cn(
          'mt-2.5 h-8 bg-white px-2.5 text-[13px] text-gray-800',
          'dark:bg-white/[0.03] dark:text-gray-100'
        )}
      />
      <InteractionActions>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!canRespond}
          onClick={() => onRespond(request.request_id, 'deny', undefined, message.trim() || 'Denied by user')}
          className="h-8 gap-1.5 px-3"
        >
          <X className="size-3.5" />
          Deny
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={!canRespond}
          onClick={() => onRespond(request.request_id, 'allow', request.input)}
          className="h-8 gap-1.5 px-3"
        >
          <Check className="size-3.5" />
          Allow
        </Button>
      </InteractionActions>
    </InteractionShell>
  );
}

function InteractionShell({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="px-1.5 py-1">
      <div className="mb-3 flex items-center gap-1.5 font-medium text-[14px] text-gray-800 dark:text-gray-100">
        <span className="flex size-5 items-center justify-center rounded-md bg-gray-100 text-gray-500 dark:bg-white/[0.07] dark:text-gray-300">
          {icon}
        </span>
        <span>{title}</span>
      </div>
      {children}
    </section>
  );
}

function InteractionSurface({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-white/10 dark:bg-white/[0.04]',
        className
      )}
    >
      {children}
    </div>
  );
}

function InteractionActions({ children }: { children: React.ReactNode }) {
  return <div className="mt-2.5 flex flex-wrap justify-end gap-2">{children}</div>;
}

function initialDrafts(questions: NormalizedQuestion[]): QuestionAnswerDrafts {
  return Object.fromEntries(questions.map((question) => [question.key, { selected: [], other: '' }]));
}

function interactionDraftStorageKey(request: InteractionRequest): string {
  return `${INTERACTION_DRAFT_KEY_PREFIX}${request.request_id}:${request.created_at_ms}`;
}

function readQuestionDrafts(key: string, questions: NormalizedQuestion[]): QuestionAnswerDrafts {
  if (typeof window === 'undefined') return initialDrafts(questions);
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return initialDrafts(questions);
    const parsed: unknown = JSON.parse(raw);
    return mergeStoredDrafts(questions, parsed);
  } catch {
    return initialDrafts(questions);
  }
}

function writeQuestionDrafts(key: string, drafts: QuestionAnswerDrafts): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(drafts));
  } catch {
    // Private mode / quota failures should not block answering the prompt
  }
}

function clearQuestionDrafts(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Storage cleanup is best-effort
  }
}

export function mergeStoredDrafts(questions: NormalizedQuestion[], stored: unknown): QuestionAnswerDrafts {
  if (!isRecord(stored)) return initialDrafts(questions);
  return Object.fromEntries(
    questions.map((question) => {
      const raw = stored[question.key];
      if (!isRecord(raw)) return [question.key, { selected: [], other: '' }];
      const allowedValues = new Set(question.options.map((option) => option.value));
      const selected = Array.isArray(raw.selected)
        ? raw.selected.filter((value): value is string => typeof value === 'string' && allowedValues.has(value))
        : [];
      const other = typeof raw.other === 'string' ? raw.other : '';
      return [question.key, { selected, other }];
    })
  );
}

function questionComplete(question: NormalizedQuestion, drafts: QuestionAnswerDrafts): boolean {
  const draft = drafts[question.key];
  if (!draft) return false;
  return draft.selected.length > 0 || draft.other.trim().length > 0;
}

function normalizeOption(raw: unknown): NormalizedQuestionOption | null {
  if (typeof raw === 'string') return { value: raw, label: raw, answerValue: raw };
  if (!isRecord(raw)) return null;
  const label = stringValue(raw.label) ?? stringValue(raw.text) ?? stringValue(raw.value);
  if (!label) return null;
  return {
    value: stringValue(raw.value) ?? label,
    label: antiochAgentText(label) ?? label,
    answerValue: label,
    description: antiochAgentText(stringValue(raw.description)),
  };
}

function selectedAnswerValues(question: NormalizedQuestion, selected: string[]): string[] {
  const answersByUiValue = new Map(question.options.map((option) => [option.value, option.answerValue]));
  return selected.map((value) => answersByUiValue.get(value) ?? value);
}

export function planText(input: Record<string, unknown>): string {
  return (
    textCandidate(input.plan) ??
    textCandidate(input.content) ??
    textCandidate(input.summary) ??
    textCandidate(input.message) ??
    textCandidate(input.description) ??
    nonEmptyJson(input) ??
    'The agent is asking to leave plan mode and start executing. Review the plan in the conversation above before approving.'
  );
}

export function permissionInputText(toolName: string, input: Record<string, unknown>): string {
  const command = stringValue(input.command) ?? stringValue(input.cmd);
  const cwd = stringValue(input.working_directory) ?? stringValue(input.cwd) ?? stringValue(input.workdir);
  if (command) {
    return `Run command${cwd ? ` in ${cwd}` : ''}:\n${command}`;
  }

  const path =
    stringValue(input.path) ??
    stringValue(input.file_path) ??
    stringValue(input.filePath) ??
    stringValue(input.notebook_path);
  if (path) {
    return `${toolDisplayName(toolName)} ${path}`;
  }

  return nonEmptyJson(input) ?? `The agent is asking to use ${toolDisplayName(toolName)}.`;
}

function textCandidate(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? antiochAgentText(trimmed) : undefined;
  }
  if (Array.isArray(value)) {
    const lines = value.map(textCandidate).filter((line): line is string => Boolean(line));
    return lines.length > 0 ? lines.join('\n') : undefined;
  }
  if (isRecord(value)) {
    return (
      textCandidate(value.plan) ??
      textCandidate(value.text) ??
      textCandidate(value.content) ??
      textCandidate(value.summary) ??
      textCandidate(value.message)
    );
  }
  return undefined;
}

function nonEmptyJson(input: Record<string, unknown>): string | undefined {
  return Object.keys(input).length > 0 ? JSON.stringify(input, null, 2) : undefined;
}

function toolDisplayName(toolName: string): string {
  if (!toolName) return 'this tool';
  // MCP tools arrive as ``mcp__<server>__<tool>``; show
  // ``list kernels (antioch-jupyter)`` so permission prompts read
  // like a sentence instead of dumping the wire-format name.
  const humanized = humanizeToolName(toolName);
  if (humanized.isMcp) return `${humanized.display} (${humanized.server})`;
  return toolName;
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function antiochAgentText(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;
  const legacyAgentName = ['C', 'laude'].join('');
  const legacySettingsPath = `~/.${legacyAgentName.toLowerCase()}/settings.json`;
  const legacySettingsDirectory = `~/.${legacyAgentName.toLowerCase()}`;
  return value
    .replace(new RegExp(escapeRegExp(legacySettingsPath), 'g'), 'the agent settings file')
    .replace(new RegExp(escapeRegExp(legacySettingsDirectory), 'g'), 'the agent settings directory')
    .replace(new RegExp(`\\b${legacyAgentName} Code\\b`, 'g'), 'Antioch Agent')
    .replace(new RegExp(`\\b${legacyAgentName}\\b`, 'g'), 'Antioch Agent');
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
